// vendor modules
import React, { Component } from "react"
import PropTypes from "prop-types"

// helper functions--this is awkward because we have segueToProfile as an action creator
// in the webapp and as a helper function on the mobile app, so one is connected
import { segueToProfile as mobileSegueToProfile } from "app/static/frontend/global-action-creators"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"

export default class Inline extends Component {
    static propTypes = {
        alwaysHasExpandControl: PropTypes.bool,
        // from component/ListView/index.js
        datum: PropTypes.object.isRequired,
        index: PropTypes.number.isRequired,
        resource: PropTypes.object, // if it renders standalone (eg in profiles), won't have resource or ID
        uniqueFlvId: PropTypes.string,
        // from containers/inlines.js
        disableFeedback: PropTypes.bool,
        formatSelect: PropTypes.func.isRequired,
        formattedInlineArgs: PropTypes.object,
        inlineId: PropTypes.number.isRequired,
        selectOptions: PropTypes.func,
        presentationComponent: PropTypes.func.isRequired,
        updateInlineAppearance: PropTypes.func,
        descriptionId: PropTypes.string,
        bylineId: PropTypes.string,
        titleId: PropTypes.string,
        // from containers/index.js
        segueToProfile: PropTypes.func,
        segueToUrl: PropTypes.func,
        // from shared/search/inline-selectors/appearance-selectors.js
        appearanceValues: PropTypes.object,
        // searchify
        isBulkActions: PropTypes.bool,
        isHighlightable: PropTypes.bool,
        isSearchify: PropTypes.bool,
        isFilterOnlySearchify: PropTypes.bool,
        isInlineBulkActionsSelected: PropTypes.bool,
        isInlineSearchifySelected: PropTypes.bool,
        isInlineSelected: PropTypes.bool,
        toggleBulkActionInlineSelectedStatus: PropTypes.func,
        toggleSearchifyInlineSelectedStatus: PropTypes.func.isRequired,
        quorumDataType: PropTypes.object,
        campaignRequiresConfirmation: PropTypes.bool,
        renderCustomFields: PropTypes.bool,
        customFields: PropTypes.array,
        // from app/static/frontend/profiles/document/DocumentProfileAboutContainer.jsx
        // from app/static/frontend/profiles-new/components/OverviewPage/index
        // from app/static/frontend/profiles-new/routes/document/selectors.js
        alwaysExpanded: PropTypes.bool,
        // from app/static/frontend/search-new/containers/Inline/Inlines.js
        customOnPress: PropTypes.func,
    }

    static defaultProps = {
        isBulkActions: false,
        isHighlightable: false,
        isSearchify: false,
        isInlineBulkActionsSelected: false,
        isInlineSearchifySelected: false,
        isInlineSelected: false,
        disableFeedback: false,
        campaignRequiresConfirmation: false,
    }

    componentDidMount() {
        // because LongInlinePresentation.js is a stateless function, rendering it impossible to use refs,
        //      we grab each Inline's corresponding 'description' DOM node using vanilla JS's getElementById()
        // stateless functions do in fact have a backing instance (they are wrapped into classes),
        //      but React still does not provide access to their refs
        //      "because [the] React team is going to make optimizations in the future"
        // using getElementById on elements in a stateless function is fine since React does not keep track of them in the VDOM
        // https://stackoverflow.com/a/38619607
        // https://github.com/facebook/react/issues/4936#issuecomment-179909980
        // do not perform any of this desktop-specific functionality in the mobile app
        if (typeof document !== "undefined") {
            const description = document.getElementById(this.props.descriptionId)

            if (description) {
                // https://developer.mozilla.org/en-US/docs/Web/API/HTMLCollection
                const descriptionImages = description.getElementsByTagName("img")
                let descriptionImageLoaded = false
                if (descriptionImages.length) {
                    Array.from(descriptionImages).forEach((img) => {
                        // once any of the images in the description finishes loading,
                        // add a "See More" button
                        img.onload = () => {
                            if (
                                (this.props.alwaysHasExpandControl ||
                                    (description && description.scrollHeight > description.clientHeight)) &&
                                !this.props.appearanceValues.get("hasExpandControl") &&
                                !descriptionImageLoaded
                            ) {
                                this.props.updateInlineAppearance(this.props.uniqueFlvId, this.props.inlineId, {
                                    hasExpandControl: true,
                                })
                                descriptionImageLoaded = true
                            }
                        }
                    })
                }
            }

            const regulationPdf = this.props.datum.getIn(["_extra", "pdf_url"])
            const attachmentData = this.props.datum.get("attachment_data")
            const regulationCommentPdf =
                attachmentData &&
                attachmentData
                    .toJS()
                    // add iff s3_url ends with .pdf
                    .filter(
                        (attachment) =>
                            attachment.s3_url.substring(attachment.s3_url.length - ".pdf".length) === ".pdf",
                    )
            const snippets = this.props.datum.get("snippets")
            const mimeType = this.props.datum.get("mime_type")

            // Inlines with pdfs and instagram posts should always have a 'See More' button
            if (
                (this.props.alwaysHasExpandControl ||
                    (description && description.scrollHeight > description.clientHeight) ||
                    // Document PDFs
                    this.props.datum.get("pdf_url") ||
                    // Regulation PDFs
                    (regulationPdf && snippets && snippets.size === 0) ||
                    (this.props.datum.get("raw_s3_url") && mimeType === "application/pdf") ||
                    // Regulation Comment PDFs
                    (regulationCommentPdf && regulationCommentPdf.length)) &&
                !this.props.appearanceValues.get("hasExpandControl")
            ) {
                // trigger an update of LongInlinePresentation.js, rendering the 'See More' dropdown where appropriate
                this.props.updateInlineAppearance(this.props.uniqueFlvId, this.props.inlineId, {
                    hasExpandControl: true,
                })
            }

            const title = document.getElementById(this.props.titleId)
            if (title && title.scrollHeight > title.clientHeight) {
                this.props.updateInlineAppearance(this.props.uniqueFlvId, this.props.inlineId, {
                    shouldShowTooltip: true,
                })
            }

            const byline = document.getElementById(this.props.bylineId)
            if (byline && byline.offsetWidth < byline.scrollWidth) {
                this.props.updateInlineAppearance(this.props.uniqueFlvId, this.props.inlineId, {
                    shouldShowBylineTooltip: true,
                })
            }
        }
    }

    // we only want to trigger a component update/re-render when we set new appearance values in the store
    shouldComponentUpdate(newProps) {
        return (
            this.props.isInlineSelected !== newProps.isInlineSelected ||
            // Immutable.js has a built in .equals method for deep comparisons
            // do not perform this check if we are on mobile
            (typeof document !== "undefined" && !this.props.appearanceValues.equals(newProps.appearanceValues))
        )
    }

    handleInlineClick = () => {
        const id = this.props.inlineId
        const qdt = this.props.quorumDataType

        if (this.props.isSearchify) {
            // if this searchify is the non-select, filter-only kind,
            // don't do anything on inline click.
            if (this.props.isFilterOnlySearchify) {
                return false
            }

            // otherwise, toggle the inline! (more complex than you'd think.)
            return this.props.toggleSearchifyInlineSelectedStatus(
                this.props.uniqueFlvId,
                id,
                this.props.isInlineSearchifySelected,
                this.props.datum.get("is_searchify_selected"),
            )
        } else if (this.props.isBulkActions) {
            // same as searchify, but in main search!
            return this.props.toggleBulkActionInlineSelectedStatus(
                this.props.uniqueFlvId,
                id,
                this.props.isInlineBulkActionsSelected,
            )
        } else {
            // we're going somewhere!
            sendUserActionPost({
                quorumDataType: qdt && qdt.value,
                regions: [this.props.datum.get("region") || this.props.datum.get("most_recent_region")],
                userActionType: DjangIO.app.useractions.types.UserActionType.search_inline_clicked,
            })

            if (this.props.customOnPress) {
                // if we need to override the default segueToProfile logic below
                return this.props.customOnPress(id, this.props)
            } else {
                // we have two different segues depending on the platform. It would be nice to standardize the segues
                return this.props.segueToProfile
                    ? this.props.segueToProfile({ id, fromSearch: true }, qdt.value)
                    : mobileSegueToProfile(qdt, id)
            }
        }
    }

    render() {
        // formattedInlineArgs are calculated by the selector, 'selectFormattedInlineData'
        // so yay caching.
        const PresentationComponent = this.props.presentationComponent
        const options =
            this.props.selectOptions && !this.props.isSearchify ? this.props.selectOptions(this.props) : undefined

        return (
            <PresentationComponent
                alwaysExpanded={this.props.alwaysExpanded}
                appearanceValues={this.props.appearanceValues}
                bylineId={this.props.bylineId}
                customFields={this.props.customFields}
                descriptionId={this.props.descriptionId}
                iconName={this.props.iconName}
                index={this.props.index}
                inlineId={this.props.inlineId}
                isBulkActions={this.props.isBulkActions}
                isBulkActionsSelected={this.props.isInlineBulkActionsSelected}
                isHighlightable={this.props.isHighlightable}
                isSearchify={this.props.isSearchify}
                isSearchifySelected={this.props.isInlineSearchifySelected}
                noResponse={this.props.isFilterOnlySearchify}
                onPress={this.handleInlineClick}
                options={options}
                quorumDataType={this.props.quorumDataType}
                renderCustomFields={this.props.renderCustomFields}
                rightSection={this.props.rightSection}
                titleId={this.props.titleId}
                uniqueFlvId={this.props.uniqueFlvId}
                updateInlineAppearance={this.props.updateInlineAppearance}
                requestChanges={this.props.requestChanges}
                {...this.props.formattedInlineArgs}
            />
        )
    }
}
